// src/components/pdf-editor/ImageOverlay.js
/**
 * Image Overlay v2 – Draggable/Resizable Image Element
 * Supports images and signatures. All positions in PDF points.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';

const HANDLE_SIZE = 8;
const CORNERS = ['nw', 'ne', 'se', 'sw'];
const CORNER_CURSORS = { nw: 'nw-resize', ne: 'ne-resize', se: 'se-resize', sw: 'sw-resize' };

const ImageOverlay = ({ element, scale, selected, onSelect, onUpdate, onInteractionEnd }) => {
  const [dragging, setDragging] = useState(false);
  const [resizing, setResizing] = useState(null);
  const startRef = useRef(null);

  const dx = element.x * scale;
  const dy = element.y * scale;
  const dw = element.w * scale;
  const dh = element.h * scale;

  /* ── Drag ── */
  const handleMouseDown = useCallback((e) => {
    e.stopPropagation();
    onSelect();
    startRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: element.x, origY: element.y,
    };
    setDragging(true);
  }, [element.x, element.y, onSelect]);

  useEffect(() => {
    if (!dragging) return;
    const move = (e) => {
      const s = startRef.current;
      if (!s) return;
      onUpdate({
        ...element,
        x: s.origX + (e.clientX - s.startX) / scale,
        y: s.origY + (e.clientY - s.startY) / scale,
      });
    };
    const up = () => { setDragging(false); onInteractionEnd(); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [dragging, element, scale, onUpdate, onInteractionEnd]);

  /* ── Resize (corners only, maintain aspect ratio) ── */
  const handleResizeStart = useCallback((e, corner) => {
    e.stopPropagation();
    e.preventDefault();
    startRef.current = {
      startX: e.clientX, startY: e.clientY,
      origX: element.x, origY: element.y,
      origW: element.w, origH: element.h,
      aspect: element.w / Math.max(element.h, 1),
    };
    setResizing(corner);
  }, [element]);

  useEffect(() => {
    if (!resizing) return;
    const move = (e) => {
      const s = startRef.current;
      if (!s) return;
      const dx2 = (e.clientX - s.startX) / scale;
      let { origX: x, origY: y, origW: w, origH: h } = s;

      if (resizing === 'se') { w = Math.max(20, s.origW + dx2); h = w / s.aspect; }
      if (resizing === 'sw') { w = Math.max(20, s.origW - dx2); h = w / s.aspect; x = s.origX + (s.origW - w); }
      if (resizing === 'ne') { w = Math.max(20, s.origW + dx2); h = w / s.aspect; y = s.origY + (s.origH - h); }
      if (resizing === 'nw') { w = Math.max(20, s.origW - dx2); h = w / s.aspect; x = s.origX + (s.origW - w); y = s.origY + (s.origH - h); }

      onUpdate({ ...element, x, y, w: Math.max(w, 20), h: Math.max(h, 10) });
    };
    const up = () => { setResizing(null); onInteractionEnd(); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, [resizing, element, scale, onUpdate, onInteractionEnd]);

  const getCornerPos = (corner) => {
    const half = HANDLE_SIZE / 2;
    return {
      top: corner.includes('n') ? -half : dh - half,
      left: corner.includes('w') ? -half : dw - half,
    };
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      onClick={(e) => { e.stopPropagation(); onSelect(); }}
      style={{
        position: 'absolute',
        left: dx, top: dy, width: dw, height: dh,
        border: selected ? '2px solid #f59e0b' : '1px solid transparent',
        borderRadius: 3,
        cursor: dragging ? 'grabbing' : 'grab',
        zIndex: selected ? 20 : 10,
        boxSizing: 'border-box',
        transition: dragging || resizing ? 'none' : 'border-color 0.15s',
      }}
    >
      <img
        src={element.src}
        alt=""
        draggable={false}
        style={{
          width: '100%', height: '100%',
          objectFit: 'fill',
          opacity: element.style?.opacity ?? 1,
          pointerEvents: 'none',
          borderRadius: 2,
        }}
      />

      {/* Corner resize handles */}
      {selected && CORNERS.map(c => (
        <div
          key={c}
          onMouseDown={(e) => handleResizeStart(e, c)}
          style={{
            position: 'absolute', ...getCornerPos(c),
            width: HANDLE_SIZE, height: HANDLE_SIZE,
            background: '#fff', border: '1.5px solid #f59e0b',
            borderRadius: 2, cursor: CORNER_CURSORS[c],
            zIndex: 30,
          }}
        />
      ))}
    </div>
  );
};

export default ImageOverlay;
